/* eslint no-underscore-dangle: 0 */
import React, { useMemo } from 'react';

// material-ui
import { Autocomplete, TextField } from '@mui/material';

export type StaffOption = { _id: string; name?: string | null; status?: string };

type StaffSelectProps = {
    /** Selected staff id; '' selects the sentinel (the caller's existing empty default). */
    value: string;
    onChange: (id: string) => void;
    staff: StaffOption[];
    /**
     * Label of the sentinel option that maps to the empty default — e.g.
     * "Account owner" for the automation assignee, "Me" for a new task's owner.
     * Its value is always the empty string, so the existing save/reload
     * contract (`taskOwnerId` / `ownerId`) is unchanged. Omit it (e.g. when
     * editing an existing task's owner) to drop the sentinel entirely.
     */
    sentinelLabel?: string;
    label?: string;
    placeholder?: string;
    helperText?: string;
    size?: 'small' | 'medium';
    disabled?: boolean;
    fullWidth?: boolean;
};

/** Sentinel value — maps to the caller's existing empty default. */
const SENTINEL = '';

/**
 * Searchable single-value staff picker (POGO-13408). Replaces the flat
 * `TextField select` behind the automation "Create task" assignee and the
 * Tasks-screen "Owner" pickers so an unbounded staff list stays usable
 * (modelled on `LeadSourceSelect`'s single-value Autocomplete mode). The
 * sentinel is always the first option and always selectable; a selected id
 * that's no longer in the list is kept visible so nothing is silently dropped.
 */
const StaffSelect = ({
    value,
    onChange,
    staff,
    sentinelLabel,
    label = 'Assignee',
    placeholder,
    helperText,
    size = 'small',
    disabled = false,
    fullWidth = true
}: StaffSelectProps) => {
    // Hide inactive staff client-side too — belt-and-suspenders over the
    // server's non-inactive predicate.
    const active = useMemo(() => staff.filter((member) => member.status !== 'inactive'), [staff]);

    const nameById = useMemo(() => {
        const map = new Map<string, string>();
        active.forEach((member) => map.set(member._id, member.name || ''));
        return map;
    }, [active]);

    const hasSentinel = sentinelLabel !== undefined;

    // Sentinel first (when present), then named staff. A selected id that isn't
    // in the list (e.g. a since-deactivated staff member) is appended so it
    // stays visible.
    const options = useMemo(() => {
        const ids = active.map((member) => member._id);
        if (value && value !== SENTINEL && !ids.includes(value)) {
            ids.push(value);
        }
        return hasSentinel ? [SENTINEL, ...ids] : ids;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active, value, hasSentinel]);

    return (
        <Autocomplete
            size={size}
            fullWidth={fullWidth}
            disabled={disabled}
            options={options}
            value={hasSentinel ? value || SENTINEL : value || null}
            onChange={(_, next) => onChange((next as string) || SENTINEL)}
            getOptionLabel={(id) => (id === SENTINEL ? sentinelLabel || '' : nameById.get(id) || id)}
            isOptionEqualToValue={(option, selected) => option === selected}
            renderInput={(params) => (
                // eslint-disable-next-line react/jsx-props-no-spreading
                <TextField {...params} label={label} placeholder={placeholder} helperText={helperText} />
            )}
        />
    );
};

export default StaffSelect;
